import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/errorUtils';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Edit, Trash2, Power, PowerOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Campaigns = ({ user, onLogout, theme, onToggleTheme }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState('');
  const { t } = useLanguage();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaignName.trim()) {
      toast.error(t('camp_name_required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/campaigns`,
        { name: newCampaignName },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(t('camp_created_ok'));
      setShowCreateModal(false);
      setNewCampaignName('');
      fetchCampaigns();
    } catch (error) {
      toast.error(extractErrorMessage(error, t('camp_create_error')));
    }
  };

  const handleToggleActive = async (campaignId, isActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/campaigns/${campaignId}`,
        { is_active: !isActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(!isActive ? t('camp_activated_ok') : t('camp_deactivated_ok'));
      fetchCampaigns();
    } catch (error) {
      toast.error(t('camp_update_error'));
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm(t('camp_delete_confirm'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/campaigns/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t('camp_deleted_ok'));
      fetchCampaigns();
    } catch (error) {
      toast.error(t('camp_delete_error'));
    }
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        user={user}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
      >
        <div className="p-6 lg:p-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
                style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                data-testid="campaigns-title"
              >
                {t('campaigns_title')}
              </h1>
              <p className="text-base" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                {campaigns.length} {t('campaigns_total')}
              </p>
            </div>
            {user.role === 'admin' && (
              <Button
                className="btn-primary"
                onClick={() => setShowCreateModal(true)}
                data-testid="create-campaign-button"
              >
                <Plus size={20} className="mr-2" />
                {t('new_campaign')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="p-6 rounded-lg border"
                style={{
                  backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                  borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
                }}
                data-testid={`campaign-card-${campaign.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3
                    className="text-xl font-bold"
                    style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                  >
                    {campaign.name}
                  </h3>
                  {campaign.is_active ? (
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: '#19803815', color: '#198038' }}
                    >
                      {t('active')}
                    </span>
                  ) : (
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: '#52525215', color: '#525252' }}
                    >
                      {t('inactive')}
                    </span>
                  )}
                </div>

                <p className="text-sm mb-4" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                  {t('campaigns_created_on')} {new Date(campaign.created_at).toLocaleDateString('it-IT')}
                </p>

                {user.role === 'admin' && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleActive(campaign.id, campaign.is_active)}
                      data-testid={`toggle-campaign-${campaign.id}`}
                    >
                      {campaign.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteCampaign(campaign.id)}
                      data-testid={`delete-campaign-${campaign.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('campaigns_create_title')}</DialogTitle>
            <DialogDescription>{t('campaigns_create_desc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="campaign-name">{t('campaign_name')}</Label>
              <Input
                id="campaign-name"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="Es: Campagna Estate 2026"
                className="mt-1"
                data-testid="campaign-name-input"
              />
            </div>
            <Button
              className="w-full btn-primary"
              onClick={handleCreateCampaign}
              data-testid="campaign-submit-button"
            >
              {t('campaigns_submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
